import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getClientId } from "../lib/clientId";
import { fetchFreshDropsLatest } from "../lib/api";
import { isSoundOn, playChime } from "../lib/alphaSound";

const useLatestDrops = () => {
  const clientId = getClientId();
  return useQuery({
    queryKey: ["freshDropsLatest", clientId],
    queryFn: () => fetchFreshDropsLatest(clientId),
    refetchInterval: 45000,
    staleTime: 30000,
  });
};

// Sidebar bubble: drops newer than the last visit to /fresh-drops.
export const useFreshDropBadge = () => {
  const q = useLatestDrops();
  const seen = Number(localStorage.getItem("fd_seen_ts") || 0);
  return (q.data?.events || []).filter((e) => (e.ts || 0) > seen).length;
};

export const markFreshDropsSeen = () => {
  localStorage.setItem("fd_seen_ts", String(Math.floor(Date.now() / 1000)));
};

// Global watcher: chime + toast anywhere in the app when a saved dev drops a new coin.
export const FreshDropWatcher = () => {
  const nav = useNavigate();
  const q = useLatestDrops();
  const toasted = useRef(Number(localStorage.getItem("fd_toast_ts") || 0));

  useEffect(() => {
    if (!toasted.current) {
      toasted.current = Math.floor(Date.now() / 1000);
      localStorage.setItem("fd_toast_ts", String(toasted.current));
    }
    const fresh = (q.data?.events || []).filter((e) => (e.ts || 0) > toasted.current);
    if (!fresh.length) return;
    toasted.current = Math.max(...fresh.map((e) => e.ts));
    localStorage.setItem("fd_toast_ts", String(toasted.current));
    if (isSoundOn()) playChime();
    const e = fresh[0];
    const extra = fresh.length > 1 ? ` (+${fresh.length - 1} more)` : "";
    toast.success(`🚀 Fresh drop: $${(e.symbol || e.mint.slice(0, 4)).toUpperCase()} by a dev you follow${extra}`, {
      id: "fresh-drop",
      duration: 12000,
      action: { label: "View", onClick: () => nav("/fresh-drops") },
    });
  }, [q.data, nav]);

  return null;
};
